import { useEffect, useContext, useState, useCallback } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { es } from 'date-fns/locale';
import UserContext from '../../context/userContext';
import CalendarioContext from '../../context/calendarioContext';
import { useNavigate } from 'react-router';
import ModalReservaciones from './ModalReservaciones';

import {
    Heading,
    useToast,
    Button,
    Alert,
    AlertIcon,
    AlertTitle,
    VStack,
    Box,
    Switch,
    Text,
    Flex,
    useDisclosure,
    Container,
    Tooltip,
} from '@chakra-ui/react';
import obtenerTemporadas from '../../services/obtenerTemporadas';
import ConfirmModal from '../util/confirmModal/confirmModal';
import { useTranslation } from 'react-i18next';
import obtenerTitular from '../../services/obtenerTitular';


const StatLine = ({ label, value }) => (
    <Text fontWeight="bold" fontSize="lg" color="teal.600">
        {label}{' '}
        <Text as="span" color="black">
        {value}
        </Text>
    </Text>
);

const FormularioReserva = () => {

    const { t } = useTranslation();

    let today = new Date();
    today = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
    );
    const nextYear = today.getFullYear() + 1; // Calcula el próximo año
    const defaultMaxDate = new Date(nextYear, 11, 31); // Por defecto, fin del próximo año

    const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
    const [liberarLoading, setLiberarLoading] = useState(false);

    // Contextos
    const { user, otraCabana, setOtraCabana, setUser } = useContext(UserContext);
    const { setDia, setReservationType, setPeople } = useContext(CalendarioContext);

    // Estados
    const [temporadas, setTemporadas] = useState([]);
    const [temporadaActual, setTemporadaActual] = useState({ tipo: '' });
    const [selectedArrivalDate, setSelectedArrivalDate] = useState(null);
    const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);

    // Estado para las Noches disponibles (local al componente)
    const [nochesAltaDisponibles, setNochesAltaDisponibles] = useState(user?.nochesAlta || 0);
    const [nochesBajaDisponibles, setNochesBajaDisponibles] = useState(user?.numeroNoches || 0);

    // Fecha máxima dinámica y por defecto
    const [maxDate, setMaxDate] = useState(defaultMaxDate);

    // Fechas de jueves en temporada baja
    const [datesJuevesBaja, setDatesJuevesBaja] = useState([]);
    const [error, setError] = useState(null);

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        console.log("ARRIVAL DATE: ", selectedArrivalDate);
    }, [selectedArrivalDate])

    useEffect(() => {
        console.log("DEPARTURE DATE: ", selectedDepartureDate);
    }, [selectedDepartureDate])

    useEffect(() => {
        if(user && user.id){
            refreshCurrentUser(user.id)
        }
    }, [])

    const refreshCurrentUser = async (userId) => {
        const res = await obtenerTitular(userId);
        if (res.status === 200 && res.data) {
          setUser(res.data.titular);
          return true;
        }
        return false;
    };

    // Chakra UI y navegación
    const {
        isOpen: isOpenModalReservaciones,
        onOpen: onOpenModalReservaciones,
        onClose: onCloseModalReservaciones,
    } = useDisclosure();
    const navigate = useNavigate();
    const toast = useToast();

    // Función para obtener todos los jueves entre dos fechas
    const getThursdaysBetweenDates = useCallback((startDate, endDate) => {
        const dates = [];
        let currentDate = new Date(startDate);

        // Ajustar al siguiente jueves si no es jueves
        currentDate.setDate(
            currentDate.getDate() + ((4 - currentDate.getDay() + 7) % 7)
        );

        while (currentDate <= endDate) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 7);
        }

        return dates;
    }, []);

    // useEffect para obtener temporadas y calcular fechas relevantes
    useEffect(() => {
        (async () => {
            try {
                const temp = await obtenerTemporadas();
                if (!temp || !temp.data) {
                    throw new Error('No se pudieron cargar las temporadas');
                }

                let currentTemps = temp.data.filter(
                    (date) => new Date(date.fechaFin) >= today
                );

                currentTemps = currentTemps.sort(
                    (a, b) => new Date(a.fechaInicio) - new Date(b.fechaInicio)
                );

                setTemporadas(currentTemps);

                // Obtener la fecha máxima de las temporadas
                if (currentTemps.length > 0) {
                    const maxTemp = currentTemps.reduce((max, item) =>
                        new Date(item.fechaFin) > new Date(max.fechaFin) ? item : max
                    );
                    setMaxDate(new Date(maxTemp.fechaFin));
                }

                // Obtener los jueves de temporada baja
                const bajas = currentTemps.filter((temp) => temp.tipo === 'BAJA');
                let juevesBaja = [];
                bajas.forEach((temp) => {
                    const inicio = new Date(temp.fechaInicio);
                    const fin = new Date(temp.fechaFin);
                    juevesBaja = juevesBaja.concat(
                        getThursdaysBetweenDates(inicio, fin)
                    );
                });
                setDatesJuevesBaja(juevesBaja);
                setError(null);
            } catch (error) {
                console.error(error);
                setError(error);
            }
        })();
    }, [getThursdaysBetweenDates]);

    // Calcular el total de noches disponibles
    const totalNoches = user.numeroNoches + user.nochesAlta;
    // Calcular los bloques de temporada baja disponibles
    const bloquesTemporadaBaja = Math.floor(user.numeroNoches / 7);

    useEffect(() => {
        if(totalNoches === 0 && user.rol === 'TITULAR') {
            setOtraCabana(true);
            setReservationType('OTRACABANA');
        }
    }, [totalNoches])

    // Cambio del estado "Otra Cabaña"
    const changeOtraCabana = () => {
        if(totalNoches === 0 && user.rol === 'TITULAR') {
            toast({
                title: t('NO_AVAILABLE_NIGHTS_WARNING'),
                status: 'warning',
                position: 'top',
                isClosable: true,
            });
            setOtraCabana(true);
            setReservationType('OTRACABANA');
            return;
        }
        setOtraCabana(!otraCabana);

        // Nota: invertimos la lógica para que si se acaba de activar "Otra Cabaña",
        // forcemos setReservationType('OTRACABANA'); de manera inmediata.
        if (!otraCabana) {
            setReservationType('OTRACABANA');
        } else {
            setReservationType(temporadaActual.tipo);
        }
    };

    // Función para obtener la temporada de una fecha específica
    const getSelectedSeason = useCallback(
        (dateToCheck) => {
            let tempAux = { tipo: '' };
            if (temporadas && temporadas.length > 0) {
                for (let i = 0; i < temporadas.length; i++) {
                    if (
                        dateToCheck >= new Date(temporadas[i].fechaInicio) &&
                        dateToCheck <= new Date((temporadas[i].fechaFin).split('T')[0] + 'T23:59:59.999Z')
                    ) {
                        tempAux = temporadas[i];
                        // console.log("Temporada Inicio: ", new Date(temporadas[i].fechaInicio));
                        // console.log("Temporada Fin: ", new Date(temporadas[i].fechaFin));
                        // console.log("Temporada Tipo: ", temporadas[i].tipo);
                        break;
                    }
                }
            }
            return tempAux;
        },
        [temporadas]
    );

    // Saber si un día es jueves
    const isThursday = useCallback((date) => {
        return date && date.getDay() === 4;
    }, []);

    // Maneja el cambio de la fecha de llegada
    const handleArrivalDateChange = (date) => {
        setSelectedArrivalDate(date);
        const temp = getSelectedSeason(date);
        setTemporadaActual(temp);

        // Auto-activate "Noches Adicionales" if NOCHEHOTELERA
        if (temp.tipo === 'NOCHEHOTELERA') {
            if (!otraCabana) { // Only activate and notify if it's not already on
                setOtraCabana(true);
                setReservationType('OTRACABANA'); // Consistent with other toggle activations
                toast({
                    title: t('AUTO_OTRACABANA_NOCHEHOTELERA_TITLE', 'Noches Adicionales Auto-activadas'),
                    description: t('AUTO_OTRACABANA_NOCHEHOTELERA_DESC', 'La temporada hotelera activa este modo para permitir mayor flexibilidad.'),
                    status: 'info',
                    position: 'top',
                    isClosable: true,
                });
            }
        } 
        // Existing logic to force "Noches Adicionales" if no availability,
        // ensure this doesn't conflict or redundantly notify if NOCHEHOTELERA already set it.
        // Add '&& temp.tipo !== 'NOCHEHOTELERA'' to these conditions.
        else if (temp.tipo === 'ALTA' && nochesAltaDisponibles <= 0) { // Removed: && temp.tipo !== 'NOCHEHOTELERA' (covered by else if)
            setOtraCabana(true);
            setReservationType('OTRACABANA');
            toast({
                title: t('NO_HIGH_SEASON_NIGHTS_TITLE', 'No tiene noches disponibles en Temporada Alta.'),
                description: t('AUTO_OTRACABANA_DESC', 'Se activó automáticamente el modo Noches Adicionales.'),
                status: 'warning',
                position: 'top',
                isClosable: true,
            });
        } else if (temp.tipo === 'BAJA' && nochesBajaDisponibles < 7) { // Removed: && temp.tipo !== 'NOCHEHOTELERA'
            setOtraCabana(true);
            setReservationType('OTRACABANA');
            toast({
                title: t('NO_LOW_SEASON_NIGHTS_TITLE', 'No tiene suficientes noches para un bloque de Temporada Baja.'),
                description: t('AUTO_OTRACABANA_DESC', 'Se activó automáticamente el modo Noches Adicionales.'),
                status: 'warning',
                position: 'top',
                isClosable: true,
            });
        }
        // -----------------------------------------------------------------------

        // Si NO está en "Otra Cabaña", ajustamos la fecha de salida por defecto
        if (!otraCabana) {
            if (temp.tipo === 'BAJA') {
                const auxFecha = new Date(date);
                auxFecha.setDate(auxFecha.getDate() + 7);
                setSelectedDepartureDate(auxFecha);
            } else if (temp.tipo === 'ALTA' || temp.tipo === 'NOCHEHOTELERA') {
                const auxFecha = new Date(date);
                auxFecha.setDate(auxFecha.getDate() + 3);
                setSelectedDepartureDate(auxFecha);
            } else {
                setSelectedDepartureDate(null);
            }
        } else {
            setSelectedDepartureDate(null);
        }
    };

    // Maneja el cambio de la fecha de salida
    const handleDepartureDateChange = (date) => {
        setSelectedDepartureDate(date);
    };

    // Filtro para fechas de llegada
    const filterArrivalDates = useCallback(
        (date) => {
            if (otraCabana) {
                // Si está activo "Otra Cabaña", no restringir fechas
                return true;
            }
            const temp = getSelectedSeason(date);
            if (temp.tipo === 'BAJA') {
                // En temporada baja, solo permitir jueves
                return datesJuevesBaja.some(
                    (d) =>
                        d.getFullYear() === date.getFullYear() &&
                        d.getMonth() === date.getMonth() &&
                        d.getDate() === date.getDate()
                );
            } else if (temp.tipo === 'ALTA' || temp.tipo === 'NOCHEHOTELERA') {
                // En alta o “hotelera”, no hay restricción de día
                return true;
            }
            return false;
        },
        [otraCabana, datesJuevesBaja, getSelectedSeason]
    );

    // Filtro para fechas de salida
    const filterDepartureDates = useCallback(
        (date) => {
            if (!selectedArrivalDate) return false;

            if (otraCabana) {
                // Si está activo "Otra Cabaña", permitir salida posterior
                return date > selectedArrivalDate;
            }

            const temp = temporadaActual;
            // Si otraCabana está activado (true), no hay límite de 3 días específico aquí.
            // Se permite seleccionar cualquier fecha posterior a la llegada (hasta el maxDate general).
            if (temp.tipo === 'ALTA' || temp.tipo === 'NOCHEHOTELERA') {
                const maxDateConstraint = new Date(selectedArrivalDate);
                maxDateConstraint.setDate(selectedArrivalDate.getDate() + 3);
                return date > selectedArrivalDate && date <= maxDateConstraint;
            } else if (temp.tipo === 'BAJA') {
                // En baja, exactamente 7 días (jueves a jueves)
                const expectedDepartureDate = new Date(selectedArrivalDate);
                expectedDepartureDate.setDate(expectedDepartureDate.getDate() + 7);
                return (
                    date.getTime() === expectedDepartureDate.getTime() &&
                    isThursday(date)
                );
            }
            return false;
        },
        [selectedArrivalDate, temporadaActual, otraCabana, isThursday]
    );

    // Validaciones y lógica final al hacer clic en "Enviar"
    const checkDates = () => {
        if (!selectedArrivalDate || !selectedDepartureDate) {
            toast({
                title: 'Debe seleccionar fechas de llegada y salida',
                status: 'error',
                position: 'top',
                isClosable: true,
            });
            return;
        }

        if (today.getTime() > selectedArrivalDate.getTime()) {
            toast({
                title: 'La fecha de llegada no puede ser anterior a hoy',
                status: 'error',
                position: 'top',
                isClosable: true,
            });
            return;
        }

        const numDias = Math.floor(
            (selectedDepartureDate.getTime() - selectedArrivalDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );

        if (numDias <= 0) {
            toast({
                title: t('FECHA_SALIDA_WARNING'),
                status: 'error',
                position: 'top',
                isClosable: true,
            });
            return;
        }

        let nuevasNochesAltaDisponibles = nochesAltaDisponibles;
        let nuevasNochesBajaDisponibles = nochesBajaDisponibles;

        // Revisar temporada de llegada
        const season = getSelectedSeason(selectedArrivalDate);

        

        const lastNight = new Date(selectedDepartureDate);
        // console.log('Selected departure date:', selectedDepartureDate);
        // console.log('Last night before adjustment:', lastNight);
        // console.log('UTC date: ', lastNight.getUTCDate());
        // console.log('ISO string:', lastNight.toISOString());
        lastNight.setDate(lastNight.getDate() - 1);
        // console.log('Last night:', lastNight);
        // console.log('Selected season:', season);
        // console.log('Last night season:', getSelectedSeason(lastNight));
        if(!otraCabana){
            if (getSelectedSeason(lastNight).tipo !== season.tipo) {
                toast({
                    title: t('CROSS_SEASON_MSG'),   // agrega esta clave a i18n
                    status: 'error',
                    position: 'top',
                    isClosable: true,
                });
                return;
            }

            if (!season.tipo) {
                toast({
                    title: t('TEMPORADA_NO_ENCONTRADA_WARNING'),
                    status: 'warning',
                    position: 'top',
                    isClosable: true,
                });
                return;
            }
        }

        // *** Ajuste Implementado ***
        // Verificar que todas las noches de la reserva estén dentro de la misma temporada
        if (!otraCabana) {
            let currentDate = new Date(selectedArrivalDate);
            let isSameSeason = true;
            while (currentDate < selectedDepartureDate) {
                const currentSeason = getSelectedSeason(currentDate);
                if (currentSeason.tipo !== season.tipo) {
                    isSameSeason = false;
                    break;
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
            if (!isSameSeason) {
                // La reserva cruza temporadas
                toast({
                    title: t('CRUZAR_TEMPORADAS_WARNING', { fechaFin: season.fechaFin.split('T')[0] }),
                    status: 'warning',
                    position: 'top',
                    isClosable: true,
                });
                return;
            }
            // *** Fin del ajuste ***
        }

        // Validaciones específicas
        if (!otraCabana) { // Solo aplicar estas validaciones de noches si el toggle "Noches Adicionales" está OFF
            if (season.tipo === 'ALTA' || season.tipo === 'NOCHEHOTELERA') {
                // Para ALTA o NOCHEHOTELERA (sin toggle), máximo 3 noches
                if (numDias < 1 || numDias > 3) {
                    toast({
                        title: t('TEMPORADA_ALTA_MAX_NOCHES_WARNING'), // Usar el mismo mensaje o uno específico para NOCHEHOTELERA si se desea
                        status: 'warning',
                        position: 'top',
                        isClosable: true,
                    });
                    return;
                }
                // Verificar disponibilidad de nochesAlta para ALTA o NOCHEHOTELERA (sin toggle)
                if (nuevasNochesAltaDisponibles < numDias) {
                    setOtraCabana(true); // Forzar toggle si no hay suficientes noches
                    setReservationType('OTRACABANA');
                    toast({
                        title: t('TEMPORADA_ALTA_NO_DISPONIBLE_WARNING', { noches: nuevasNochesAltaDisponibles }),
                        status: 'warning',
                        position: 'top',
                        isClosable: true,
                    });
                    return;
                }
                nuevasNochesAltaDisponibles -= numDias;
            }
            // La lógica original para 'BAJA' ya está dentro de un if !otraCabana, por lo que se mantiene igual.
        }
        // La lógica de 'BAJA' se mueve fuera del if !otraCabana general para que se aplique
        // solo si es BAJA y !otraCabana, independiente de las validaciones de ALTA/NOCHEHOTELERA
        if (season.tipo === 'BAJA' && !otraCabana) {
            // 7 noches exactas, de jueves a jueves
            if (numDias !== 7) {
                toast({
                    title: t('TEMPORADA_BAJA_BLOQUE_WARNING'),
                    status: 'warning',
                    position: 'top',
                    isClosable: true,
                });
                return;
            }
            if (
                !isThursday(selectedArrivalDate) ||
                !isThursday(selectedDepartureDate)
            ) {
                toast({
                    title: t('TEMPORADA_BAJA_JUEVES_WARNING'),
                    status: 'warning',
                    position: 'top',
                    isClosable: true,
                });
                return;
            }
            if (nuevasNochesBajaDisponibles < numDias) {
                // Forzar noches adicionales
                setOtraCabana(true);
                setReservationType('OTRACABANA');
                toast({
                    title: 'No tiene noches disponibles para reservar en Temporada Baja. Se activó automáticamente Noches Adicionales.',
                    status: 'warning',
                    position: 'top',
                    isClosable: true,
                });
                return;
            }
            nuevasNochesBajaDisponibles -= numDias;
        }

        if (season.tipo === 'NOCHEHOTELERA') {
            // Se maneja costo adicional en backend, sin descontar noches
            // Igualmente no se permite cruzar temporada
            setReservationType('NOCHEHOTELERA');
        }

        // Actualizamos estados si pasa validaciones
        setNochesAltaDisponibles(nuevasNochesAltaDisponibles);
        setNochesBajaDisponibles(nuevasNochesBajaDisponibles);

        // Guardamos la reserva en nuestro contexto (setDia)
        setDia({
            allDay: true,
            startStr: `${selectedArrivalDate.getFullYear()}-${
                (selectedArrivalDate.getMonth() + 1) < 10 ? `0${selectedArrivalDate.getMonth() + 1}` : selectedArrivalDate.getMonth() + 1
            }-${selectedArrivalDate.getDate() < 10 ? `0${selectedArrivalDate.getDate()}` : selectedArrivalDate.getDate()}`,
            endStr: `${selectedDepartureDate.getFullYear()}-${
                (selectedDepartureDate.getMonth() + 1) < 10 ? `0${selectedDepartureDate.getMonth() + 1}` : selectedDepartureDate.getMonth() + 1
            }-${selectedDepartureDate.getDate() < 10 ? `0${selectedDepartureDate.getDate()}` : selectedDepartureDate.getDate()}`,
            end: selectedDepartureDate,
            start: selectedArrivalDate,
        });

        // Definir el tipo de reserva (Temporada o Adicional)
        if (!otraCabana) {
            setReservationType(season.tipo);
        } else {
            setReservationType('OTRACABANA');
        }
        setSaving(true);
        // Navegar a la siguiente pantalla
        navigate('/reservar');
    };

    const requestLiberarNoches = async () => {
        // setLiberarLoading(true);
        // try {
        //     const response = await liberarMisNoches();
        //     if (response.status === 200) {
        //         toast({
        //             title: t('NOCHES_LIBERADAS_SUCCESS'),
        //             status: 'success',
        //             position: 'top',
        //             isClosable: true,
        //         });
        //     } else {
        //         toast({
        //             title: t('NOCHES_LIBERADAS_ERROR'),
        //             status: 'error',
        //             position: 'top',
        //             isClosable: true,
        //         });
        //     }
        // } catch (error) {
        //     console.error(error);
        //     toast({
        //         title: 'Error al liberar noches',
        //         status: 'error',
        //         position: 'top',
        //         isClosable: true,
        //     });
        // } finally {
        //     setLiberarLoading(false);
        //     setIsOpenConfirmModal(false);
        // }
    }

    // Mostrar error si falló la carga de temporadas
    if (error) {
        return (
            <Box p={4}>
                <Alert status="error">
                    <AlertIcon />
                    <AlertTitle>
                        Ocurrió un error al cargar las temporadas
                    </AlertTitle>
                    <Text>{error.message}</Text>
                </Alert>
            </Box>
        );
    }

    return (
        <Box as="main" bg="gray.50" minH="100vh">
            <ConfirmModal
                title={t('LIBERAR_NOCHES_CONFIRM_TITLE')}
                message={t('LIBERAR_NOCHES_CONFIRM_MESSAGE')}
                isOpen={isOpenConfirmModal}
                onClose={() => setIsOpenConfirmModal(false)}
                onConfirm={() => {
                    requestLiberarNoches();
                }}
                isLoading={liberarLoading}
            />
            <Container maxW="container.md" px={[2, 4]} py={[4, 6]}>
                {/* Alerta si el titular no tiene noches disponibles */}
                {totalNoches === 0 && user.rol === 'TITULAR' && (
                    <Alert
                        status="error"
                        alignItems="center"
                        justifyContent="center"
                        mb={6}
                        borderRadius="md"
                    >
                        <AlertIcon />
                        <AlertTitle fontSize="md" textAlign="center">
                            {t('NO_AVAILABLE_NIGHTS_WARNING')}
                        </AlertTitle>
                    </Alert>
                )}

                {/* Vista NO admin */}
                {!user.admin && (
                    <Box
                        p={[4, 6]}
                        bg="white"
                        borderRadius="md"
                        boxShadow="sm"
                        border="1px"
                        borderColor="gray.200"
                        mb={6}
                    >
                        <Heading size="md" mb={4}>
                            {t('SELECT_DATES_TO_RESERVE')}
                        </Heading>
                        <Flex direction="column" gap={2}>
                            <Text
                                fontWeight="bold"
                                fontSize="lg"
                                color="teal.600"
                            >
                                {t('TOTAL_NIGHTS_AVAILABLE')}{' '}
                                <Text as="span" color="black">
                                    {totalNoches}
                                </Text>
                            </Text>
                            <Text
                                fontWeight="bold"
                                fontSize="lg"
                                color="teal.600"
                            >
                                {t('HIGH_SEASON_NIGHTS_AVAILABLE')}{' '}
                                <Text as="span" color="black">
                                    {user.nochesAlta}
                                </Text>
                            </Text>
                            <Text
                                fontWeight="bold"
                                fontSize="lg"
                                color="teal.600"
                            >
                                {t('LOW_SEASON_NIGHTS_AVAILABLE')}{' '}
                                <Text as="span" color="black">
                                    {user.numeroNoches}
                                </Text>
                            </Text>
                            <Text
                                fontWeight="bold"
                                fontSize="lg"
                                color="teal.600"
                            >
                                {t('LOW_SEASON_BLOCKS_AVAILABLE')}{' '}
                                <Text as="span" color="black">
                                    {bloquesTemporadaBaja} {t('OF_4')}
                                </Text>
                            </Text>
                        </Flex>
                    </Box>
                )}

                <ModalReservaciones
                    isOpen={isOpenModalReservaciones}
                    onClose={onCloseModalReservaciones}
                    changeOtraCabana={changeOtraCabana}
                />

                {/* Mensaje de temporada */}
                {!otraCabana && temporadaActual.tipo && (
                    <Box
                        display="flex"
                        alignItems="center"
                        backgroundColor={
                            temporadaActual.tipo === 'ALTA'
                                ? 'red.300'
                                : temporadaActual.tipo === 'NOCHEHOTELERA'
                                    ? 'green.300'
                                    : 'blue.300'
                        }
                        rounded="md"
                        p={4}
                        mb={6}
                    >
                        {temporadaActual.tipo === 'ALTA' && (
                            <Text fontWeight="medium">
                                {t('LOW_SEASON_RESERVATION_1')} <strong>{t('HIGH_SEASON')}</strong>. 
                                {t('LOW_SEASON_RESERVATION_2')}{' '}
                                <strong>{user.nochesAlta} {t('NIGHTS')}</strong> {t('LOW_SEASON_RESERVATION_3')}
                            </Text>
                        )}
                        {temporadaActual.tipo === 'BAJA' && (
                            <Text fontWeight="medium">
                                {t('RESERVING_LOW_SEASON_PART_1')} <strong>{t('LOW_SEASON')}</strong>.{' '}
                                {t('RESERVING_LOW_SEASON_PART_2')} <strong>{t('LOW_SEASON_BLOCKS')}</strong>{' '}
                                {t('RESERVING_LOW_SEASON_PART_3')} <strong>{t('THURSDAY')}</strong>.
                            </Text>
                        )}
                        {temporadaActual.tipo === 'NOCHEHOTELERA' && (
                            <Text fontWeight="medium">
                                {t('RESERVING_ADDITIONAL_NIGHTS_PART_1')} <strong>{t('ADDITIONAL_NIGHTS')}</strong>.{' '}
                                {t('RESERVING_ADDITIONAL_NIGHTS_PART_2')}
                            </Text>
                        )}
                    </Box>
                )}

                <Box
                    bg="white"
                    p={[4, 6]}
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.200"
                    boxShadow="sm"
                >
                    <VStack spacing={4} align="stretch">
                        <Text fontSize="lg" fontWeight="semibold">
                            {t('CABIN')}: {user.tipoCabana}
                        </Text>

                        <Flex
                            justify="space-between"
                            alignItems="center"
                            bg="gray.100"
                            p={3}
                            borderRadius="md"
                        >
                            <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="gray.700"
                            >
                                {t('ADDITIONAL_NIGHTS')}
                            </Text>
                            <Switch
                                colorScheme="green"
                                size="lg"
                                isChecked={otraCabana}
                                onChange={changeOtraCabana}
                                isDisabled={user.moroso}
                            />
                        </Flex>

                        {/* Inputs de fecha (entrada y salida) */}
                        <Box>
                            <Text fontWeight="medium">{t('ARRIVAL_DATE')}</Text>
                            <ReactDatePicker
                                selected={selectedArrivalDate}
                                onChange={handleArrivalDateChange}
                                filterDate={filterArrivalDates}
                                minDate={new Date()}
                                maxDate={maxDate}
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('FECHA_ENTRADA_PLACEHOLDER')}
                                className="chakra-input css-n11k94"
                                locale={es}
                            />
                            {/* <Tooltip label={t('RULES_TOOLTIP')}>
                                <Text fontSize="sm" color="gray.500">
                                    {t('RULES_TOOLTIP_HINT')}
                                </Text>
                            </Tooltip> */}
                        </Box>

                        <Box>
                            <Text fontWeight="medium">{t('DEPARTURE_DATE')}</Text>
                            <ReactDatePicker
                                selected={selectedDepartureDate}
                                onChange={handleDepartureDateChange}
                                filterDate={filterDepartureDates}
                                minDate={selectedArrivalDate || new Date()}
                                maxDate={maxDate}
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('FECHA_SALIDA_PLACEHOLDER')}
                                className="chakra-input css-n11k94"
                                locale={es}
                                disabled={
                                    (temporadaActual.tipo === 'BAJA' && !otraCabana) ||
                                    !selectedArrivalDate
                                }
                            />
                        </Box>

                        <Button
                            colorScheme="orange"
                            onClick={checkDates}
                            alignSelf="flex-end"
                            isLoading={saving}
                            mt={2}
                        >
                            {t('SEND')}
                        </Button>
                    </VStack>
                </Box>
            </Container>
        </Box>
    );
};

export default FormularioReserva;